export interface ModulePermissions {
  view: boolean;
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
  send?: boolean;
  export?: boolean;
  manageUsers?: boolean;
}

export interface Permissions {
  students: ModulePermissions;
  prospects: ModulePermissions;
  leads: ModulePermissions;
  courses: ModulePermissions;
  lecturers: ModulePermissions;
  finance: ModulePermissions;
  communication: ModulePermissions;
  tasks: ModulePermissions;
  reports: ModulePermissions;
  settings: ModulePermissions;
  accounting: ModulePermissions;
  [key: string]: ModulePermissions;
}

export const defaultPermissions: Record<string, Permissions> = {
  admin: {
    students: { view: true, add: true, edit: true, delete: true },
    prospects: { view: true, add: true, edit: true, delete: true },
    leads: { view: true, add: true, edit: true, delete: true },
    courses: { view: true, add: true, edit: true, delete: true },
    lecturers: { view: true, add: true, edit: true, delete: true },
    finance: { view: true, add: true, edit: true, delete: true },
    communication: { view: true, send: true },
    tasks: { view: true, add: true, edit: true, delete: true },
    reports: { view: true, export: true },
    settings: { view: true, edit: true, manageUsers: true },
    accounting: { view: true, add: true, edit: true, delete: true },
    attendance: { view: true, add: true, edit: true, delete: true },
    grades: { view: true, add: true, edit: true, delete: true },
  },
  secretary: {
    students: { view: true, add: true, edit: true, delete: false },
    prospects: { view: true, add: true, edit: true, delete: false },
    leads: { view: true, add: true, edit: true, delete: false },
    courses: { view: true, add: false, edit: false, delete: false },
    lecturers: { view: true, add: false, edit: false, delete: false },
    finance: { view: false, add: false, edit: false, delete: false },
    communication: { view: true, send: true },
    tasks: { view: true, add: true, edit: true, delete: false },
    reports: { view: true, export: true },
    settings: { view: false, edit: false, manageUsers: false },
    accounting: { view: false, add: false, edit: false, delete: false },
    attendance: { view: true, add: true, edit: true, delete: false },
    grades: { view: true, add: true, edit: true, delete: false },
  },
  lecturer: {
    students: { view: true, add: false, edit: false, delete: false },
    prospects: { view: false, add: false, edit: false, delete: false },
    leads: { view: false, add: false, edit: false, delete: false },
    courses: { view: true, add: false, edit: false, delete: false },
    lecturers: { view: false, add: false, edit: false, delete: false },
    finance: { view: false, add: false, edit: false, delete: false },
    communication: { view: false, send: false },
    tasks: { view: true, add: true, edit: false, delete: false },
    reports: { view: false, export: false },
    settings: { view: false, edit: false, manageUsers: false },
    accounting: { view: false, add: false, edit: false, delete: false },
    attendance: { view: true, add: true, edit: true, delete: false },
    grades: { view: true, add: true, edit: true, delete: false },
  },
  viewer: {
    students: { view: true, add: false, edit: false, delete: false },
    prospects: { view: true, add: false, edit: false, delete: false },
    leads: { view: true, add: false, edit: false, delete: false },
    courses: { view: true, add: false, edit: false, delete: false },
    lecturers: { view: true, add: false, edit: false, delete: false },
    finance: { view: false, add: false, edit: false, delete: false },
    communication: { view: true, send: false },
    tasks: { view: true, add: false, edit: false, delete: false },
    reports: { view: true, export: false },
    settings: { view: false, edit: false, manageUsers: false },
    accounting: { view: false, add: false, edit: false, delete: false },
    attendance: { view: true, add: false, edit: false, delete: false },
    grades: { view: true, add: false, edit: false, delete: false },
  },
};

// Map sidebar keys to permission module keys
export const sidebarPermissionMap: Record<string, keyof Permissions> = {
  dashboard: 'students', // dashboard is always visible
  leads: 'leads',
  prospects: 'prospects',
  students: 'students',
  tasks: 'tasks',
  courses: 'courses',
  schedule: 'courses',
  lecturers: 'lecturers',
  finance: 'finance',
  communication: 'communication',
  reports: 'reports',
  accounting: 'accounting',
  settings: 'settings',
  registrationForms: 'students',
};

export function canAccess(permissions: Permissions | null, module: keyof Permissions, action: string = 'view'): boolean {
  if (!permissions) return false;
  const mod = permissions[module];
  if (!mod) return false;
  return !!(mod as any)[action];
}
